<template>
  <div class="c-app" :class="{ 'c-app--fullscreen': isFullscreen }">
    <TheSidebar/>
    <CWrapper>
      <TheHeader v-if="!isFullscreen"/>
      <div class="c-body">
        <main class="">
          <CContainer fluid>
            <transition name="fade">
              <keep-alive :max="20" :exclude="cacheExclude">
                <router-view :key="viewKey"></router-view>
              </keep-alive>
            </transition>
          </CContainer>
        </main>
        <TheFooter v-if="!isFullscreen"/>
      </div>
    </CWrapper>

    <!-- Command palette (Cmd/Ctrl+K). Mounted here rather than in a route so
         it's reachable from every authenticated page, fullscreen ones included. -->
    <AppSpotlight/>

    <!-- Mandatory staff-details gate. Lives here rather than in a route so it
         covers every authenticated page, including fullscreen ones. -->
    <ProfileCompletionModal/>
  </div>
</template>

<script>
import TheSidebar from './TheSidebar'
import TheHeader from './TheHeader'
import TheFooter from './TheFooter'
import ProfileCompletionModal from '@/components/ProfileCompletionModal.vue'
import AppSpotlight from '@/components/AppSpotlight.vue'

// Pages whose component instance must never be cached. Anything that mutates
// data, holds a form, or shows the detail of a single record needs a fresh
// mount on every visit — otherwise users could submit stale form state. Add
// new Create*/Edit*/*Form components here when you introduce them.
const NO_CACHE_VIEWS = [
  'Register',
  'User', 'EditUser',
  'Role', 'CreateRole', 'EditRole',
  'SettingsCreateRole',
  'SettingsCreateOrganisation', 'SettingsEditOrganisation',
  'SettingsCreateDepartment', 'SettingsEditDepartment',
  'CreateMenu', 'EditMenu', 'DeleteMenu', 'CreateMenuElement',
  'CreateBread', 'EditBread', 'DeleteBread', 'CreateBreadFieldCard', 'ShowBreadFieldCard',
  'CreateEmailTemplate', 'EditEmailTemplate', 'SendEmail',
  'CreateResources', 'CreateResourceField', 'ResourceDetails',
  'CustomerDetails',
  'OrderForm', 'CallingForm', 'FieldSalesForm', 'POS',
  'FeedbackCall', 'MDIFeedbackCall',
  'VerifyZohoInvoices',
  'HmsImports',
  'IncentivePreview',
  'TeleSalesAccounts',
  'ScannerReadyToShip',
  'ScannerReturns',
  'VppPayments',
  'ProfileSettings',
  'InfoEditor', 'InfoDocument',
  'CreateVaultEntry', 'EditVaultEntry',
  'SettingsDomainDns',
  'Login', 'Page404', 'Page500'
]

export default {
  name: 'TheContainer',
  components: {
    TheSidebar,
    TheHeader,
    TheFooter,
    ProfileCompletionModal,
    AppSpotlight
  },
  data () {
    return {
      cacheExclude: NO_CACHE_VIEWS,
      priorSidebarMinimize: null,
    }
  },
  computed: {
    activeOrganisationId () {
      return this.$store.state.activeOrganisationId
    },
    isFullscreen () {
      return Boolean(this.$route.meta && this.$route.meta.fullscreen)
    },
    viewKey () {
      // Pages with their own nested layout (e.g. Settings, which has its own
      // child sidebar) collapse to a single stable key so navigating between
      // child tabs doesn't remount the layout shell. Extend this list when
      // introducing another nested-layout page.
      const path = this.$route.path
      const stableBase = path === '/settings' || path.startsWith('/settings/')
        ? '/settings'
        : this.$route.fullPath
      return `${stableBase}::${this.activeOrganisationId}`
    }
  },
  watch: {
    // Auto-minimize the sidebar on fullscreen pages (e.g. /pos) and restore
    // the prior state when leaving. We snapshot the user's prior preference
    // so a manually expanded sidebar is brought back unchanged.
    isFullscreen: {
      immediate: true,
      handler (next, prev) {
        if (next && !prev) {
          this.priorSidebarMinimize = this.$store.state.sidebarMinimize
          this.$store.commit('set', ['sidebarMinimize', true])
        } else if (!next && prev && this.priorSidebarMinimize !== null) {
          this.$store.commit('set', ['sidebarMinimize', this.priorSidebarMinimize])
          this.priorSidebarMinimize = null
        }
      }
    }
  }
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* Fullscreen pages (e.g. POS) reclaim the entire viewport — no header,
   no container padding, so the page owns the full screen edge-to-edge. */
.c-app--fullscreen ::v-deep .c-body {
  padding-top: 0;
}

.c-app--fullscreen ::v-deep .c-body > main {
  padding: 0;
}

.c-app--fullscreen ::v-deep .container-fluid {
  padding-left: 0;
  padding-right: 0;
}
</style>
